import { useCallback, useMemo } from 'react';
import { noop } from 'lodash';
import styled from 'styled-components';
import { useApolloClient } from '@apollo/client';
import {
  type AccountTier,
  WorkspaceNavInfo,
  type WorkspaceNavInfoFragment,
} from '@frameio/graphql-client/generated';
import {
  Box,
  List,
  ListItem,
  Text,
  theme,
  Tooltip,
  useMeasure,
  VirtualList,
} from '@frameio/vapor';
import { IconPlus } from '@frameio/vapor-icons';
import { LoadingContainer } from '@frameio/vapor-migration';
import { INVITED_TO_ID } from '../../../constants';
import { useShowCreateWorkspaceModal } from '../../../settings';
import { useWorkspaceLimit } from '../../hooks';
import { InvitedToNavItem, StickyItems, WorkspaceNavItem } from './components';
import { StyledListItemContent } from './components/NavItem.styles';
import TEXT from './generated/TEXT/WorkspacesNav';

const ADD_WORKSPACE_KEY = 'add-workspace';
const CREATE_WORKSPACE_ID = 'create-button';

export type WorkspacesNavProps = {
  accountTier?: AccountTier | null;
  canCreateWorkspaces: boolean;
  error?: any;
  onNavItemClick: (workspaceId: string) => void;
  shouldRender: boolean;
  showInvitedToNavItem: boolean;
  workspaceIds: Array<WorkspaceNavInfoFragment['id']>;
  loadingMoreWorkspaces: boolean;
  fetchNext?: () => void;
  numWorkspaces: number;
};

type VirtualListItem = {
  id: string;
  name: string;
};

export const WorkspacesNav = ({
  accountTier: __accountTier,
  canCreateWorkspaces,
  error,
  onNavItemClick,
  shouldRender,
  showInvitedToNavItem,
  workspaceIds,
  loadingMoreWorkspaces,
  fetchNext = noop,
  numWorkspaces,
}: WorkspacesNavProps) => {
  const [ref, bounds] = useMeasure();
  const isBottomSticky = ITEM_HEIGHT * numWorkspaces > bounds.height;
  const showCreateWorkspaceModal = useShowCreateWorkspaceModal();
  const {
    shouldShowCreateButton,
    isCreateButtonDisabled,
    limitReachedTooltip,
  } = useWorkspaceLimit();
  const showCreateWorkspace = shouldShowCreateButton(canCreateWorkspaces);
  const isButtonDisabled = isCreateButtonDisabled(numWorkspaces);
  const handleSettingsClick = useCallback(() => {
    showCreateWorkspaceModal({
      trackingSource: 'WorkspaceSettingsFromWorkspace',
    });
  }, [showCreateWorkspaceModal]);
  const handleButtonClick = useCallback(() => {
    if (!isButtonDisabled) {
      handleSettingsClick();
    }
  }, [isButtonDisabled, handleSettingsClick]);

  const newWorkspaceTitle = TEXT.newWorkspace.title;
  const scrollToInvitedTo = TEXT.scrollToInvitedTo;
  const scrollToWorkspace = TEXT.scrollToWorkspace;

  const addWorkspaceButton = useMemo(
    () => (
      <Tooltip disabled={!isButtonDisabled} title={limitReachedTooltip}>
        <span
          onClick={handleButtonClick}
          style={{ cursor: isButtonDisabled ? 'not-allowed' : 'pointer' }}
        >
          <VirtualList.NavRow.Container
            key={ADD_WORKSPACE_KEY}
            data-testid={ADD_WORKSPACE_KEY}
            disabled={isButtonDisabled}
            id={ADD_WORKSPACE_KEY}
          >
            <VirtualList.NavRow.Icon Icon={IconPlus} />
            <VirtualList.NavRow.Label
              enableTooltip={true}
              label={newWorkspaceTitle}
            />
          </VirtualList.NavRow.Container>
        </span>
      </Tooltip>
    ),
    [
      handleButtonClick,
      isButtonDisabled,
      limitReachedTooltip,
      newWorkspaceTitle,
    ]
  );

  const renderItem = useCallback(
    ({ id }: VirtualListItem) => {
      if (id === CREATE_WORKSPACE_ID) {
        return (
          <ListItem
            key={ADD_WORKSPACE_KEY}
            data-testid={ADD_WORKSPACE_KEY}
            textValue={TEXT.addWorkspace}
          >
            <Tooltip disabled={!isButtonDisabled} title={limitReachedTooltip}>
              <span
                onClick={handleButtonClick}
                style={{
                  opacity: isButtonDisabled ? 0.5 : 1,
                  cursor: isButtonDisabled ? 'not-allowed' : 'pointer',
                }}
              >
                <StyledListItemContent
                  adornmentBefore={<IconPlus />}
                  label={newWorkspaceTitle}
                />
              </span>
            </Tooltip>
          </ListItem>
        );
      }
      if (id === INVITED_TO_ID) {
        return (
          <ListItem key={id} textValue={scrollToInvitedTo}>
            <InvitedToNavItem />
          </ListItem>
        );
      }
      return (
        <ListItem key={id} textValue={scrollToWorkspace}>
          <WorkspaceNavItem workspaceId={id} />
        </ListItem>
      );
    },
    [
      handleButtonClick,
      isButtonDisabled,
      limitReachedTooltip,
      newWorkspaceTitle,
      scrollToInvitedTo,
      scrollToWorkspace,
    ]
  );

  const { cache } = useApolloClient();
  const items: Array<VirtualListItem> = useMemo(() => {
    const baseIds = workspaceIds?.map((id) => {
      const data = cache.readFragment({
        fragment: WorkspaceNavInfo,
        fragmentName: 'WorkspaceNavInfo',
        id: cache.identify({ __typename: 'Workspace', id }),
        returnPartialData: true,
      });
      return {
        id,
        name: data?.name ?? '',
      };
    });
    if (showInvitedToNavItem) {
      baseIds.push({ id: INVITED_TO_ID, name: 'Invited To' });
    }
    if (showCreateWorkspace && !isBottomSticky) {
      return [...baseIds, { id: CREATE_WORKSPACE_ID, name: 'Add Workspace' }];
    }
    return baseIds;
  }, [
    isBottomSticky,
    showCreateWorkspace,
    showInvitedToNavItem,
    workspaceIds,
    cache,
  ]);

  return (
    <WorkspacesNavContainer>
      <WorkspacesPanelHeader />
      <LoadingContainer
        data-testid-error="workspaces-nav-error"
        errorMessage={error ? TEXT.error : undefined}
        flexGrow="1"
        isLoading={!shouldRender}
        overflow="hidden"
      >
        {shouldRender && (
          <Box ref={ref} display="flex" flexDirection="column" height="100%">
            <List
              animate={false}
              aria-label={TEXT.workspacesNavAriaLabel}
              disabledKeys={[ADD_WORKSPACE_KEY, CREATE_WORKSPACE_ID]}
              estimatedHeaderHeight={ITEM_HEIGHT}
              items={items}
              linkBehavior="action"
              loading={loadingMoreWorkspaces ? 'loading-more' : undefined}
              onBoundary={(boundary) => {
                if (boundary === 'bottom') {
                  fetchNext();
                }
              }}
              onSelectionChange={(key) => {
                const selectedWorkspaceId = Array.from(key)[0] as string;
                onNavItemClick(selectedWorkspaceId);
              }}
              selectionBehavior="replace"
              selectionMode="single"
              variant="nav"
            >
              {renderItem}
            </List>

            {isBottomSticky && showCreateWorkspace && (
              <StickyItems>{addWorkspaceButton}</StickyItems>
            )}
          </Box>
        )}
      </LoadingContainer>
    </WorkspacesNavContainer>
  );
};

export const WorkspacesNavLoading = () => {
  return (
    <WorkspacesNavContainer>
      <WorkspacesPanelHeader />
      <LoadingContainer flexGrow="1" isLoading={true} overflow="hidden" />
    </WorkspacesNavContainer>
  );
};

const WorkspacesPanelHeader = () => (
  <Box
    display="flex"
    flexGrow="0"
    paddingBottom={TITLE_PADDING_BOTTOM}
    paddingLeft={TITLE_PADDING}
    paddingTop={TITLE_PADDING}
  >
    <Text color="tertiary" id="workspaceNavTitle" variant="secondary">
      {TEXT.navTitle}
    </Text>
  </Box>
);

const WorkspacesNavContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;

  ${theme.mediaQueries.tablet} {
    left: 0;
    position: absolute;
    right: 0;
  }
`;

TEXT.DEFINE({
  /** Aria label for the workspaces navigation */
  workspacesNavAriaLabel: 'Workspaces Navigation',
  /** Aria label for the add workspace button */
  addNewWorkspace: 'Add new workspace',
  /** Text label for the add workspace button */
  addWorkspace: 'Add workspace',
  /** Error message when workspace list fails to load */
  error: 'An error occurred retrieving workspaces list',
  /** Navigation title for workspaces section */
  navTitle: 'Workspaces',
  /** Text labels for the new workspace entry */
  newWorkspace: {
    /** Title label for the new workspace item */
    title: 'New Workspace',
  },
  /** Aria label for scrolling to the invited-to section */
  scrollToInvitedTo: 'Scroll to Invited to',
  /** Aria label for scrolling to a specific workspace */
  scrollToWorkspace: 'Scroll to Workspace',
});

const ITEM_HEIGHT = 32;
const TITLE_PADDING = 16;
const TITLE_PADDING_BOTTOM = 8;
